// Auth and vault-key endpoints. Shapes match the Worker's src/auth.ts and src/vault-keys.ts.

import { API_BASE_URL, apiDelete, apiGet, apiPost, apiPostAnonymous, apiPut } from './client';

export interface OreiaUser {
  id: string;
  email: string;
  name?: string;
  image?: string;
  emailVerified?: boolean;
}

interface AuthResponse {
  token: string;
  user: OreiaUser;
}

interface MeResponse {
  user: {
    sub?: string;
    id?: string;
    email: string;
    name?: string;
    image?: string;
    email_verified?: boolean;
  };
}

/** /auth/me returns JWT-shaped claims; normalise to the app's user model. */
function normalizeUser(claims: MeResponse['user']): OreiaUser {
  return {
    id: claims.sub || claims.id || '',
    email: claims.email,
    name: claims.name,
    image: claims.image,
    emailVerified: claims.email_verified,
  };
}

export async function fetchCurrentUser(): Promise<OreiaUser> {
  const res = await apiGet<MeResponse>('/auth/me');
  return normalizeUser(res.user);
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  return apiPostAnonymous<AuthResponse>('/auth/login', { email, password });
}

export async function signup(input: {
  email: string;
  password: string;
  name?: string;
  inviteCode?: string;
}): Promise<AuthResponse> {
  return apiPostAnonymous<AuthResponse>('/auth/signup', {
    email: input.email,
    password: input.password,
    name: input.name,
    inviteCode: input.inviteCode,
  });
}

/**
 * Confirm a password without disturbing the current session. Used before wrapping the vault key
 * with a password the user typed from memory, so a typo can't produce a vault their real password
 * will never open. The returned token is deliberately discarded — the existing session stays.
 */
export async function verifyPassword(email: string, password: string): Promise<boolean> {
  try {
    await apiPostAnonymous<AuthResponse>('/auth/login', { email, password });
    return true;
  } catch {
    return false;
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiPostAnonymous('/auth/forgot-password', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await apiPostAnonymous('/auth/reset-password', { token, password });
}

export async function resendVerification(email: string): Promise<void> {
  await apiPostAnonymous('/auth/resend-verification', { email });
}

export async function updateProfileName(name: string): Promise<void> {
  await apiPut('/auth/profile', { name });
}

/**
 * Change the address on the account.
 *
 * The new address does not take effect when this resolves — the Worker sends a confirmation link to
 * it first, so an address typed with a mistake cannot lock anyone out of their own account. The UI has
 * to say so, or the old email continuing to show reads as a failure.
 */
export async function changeEmail(email: string): Promise<void> {
  await apiPut('/auth/email', { email });
}

export async function deleteAccount(): Promise<void> {
  await apiDelete('/auth/account');
}

/**
 * Start the Google OAuth redirect. The Worker bounces back to FRONTEND_URL/login?token=… once
 * consent completes, so there is no popup and no client secret in the browser.
 */
export function googleSignInURL(): string {
  return `${API_BASE_URL}/auth/google`;
}

// ── Vault keys ─────────────────────────────────────────────────

export interface VaultKeysResponse {
  passwordWrappedDek: string | null;
  recoveryWrappedDek: string | null;
  dekSalt: string | null;
}

export async function fetchVaultKeys(): Promise<VaultKeysResponse> {
  return apiGet<VaultKeysResponse>('/api/vault/keys');
}

export async function storeVaultKeys(payload: {
  passwordWrappedDek: string;
  recoveryWrappedDek: string;
  dekSalt: string;
  rawDek: string;
}): Promise<void> {
  await apiPost('/api/vault/keys', payload);
}

export async function rewrapVaultKeys(payload: {
  passwordWrappedDek: string;
  dekSalt: string;
}): Promise<void> {
  await apiPost('/api/vault/keys/rewrap', payload);
}
